import { useContext, useMemo } from "react";
import type { LabelKeyObject } from "react-csv/lib/core";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { TableOptionsContext } from "@ryde/controls";
import { useReportId, useReportsContext } from "@ryde/redux";
import {
  formatReportExportCellValue,
  getReportExportColumns,
  getReportFooterValue,
  reportsTableColumnsOrderStateAF,
  reportsTableColumnsSortingAF,
  reportsTableColumnsVisibilityAF,
  rideFilterSearchAtom,
  useTimezone,
} from "@ryde/shared";

export const useReportDownload = () => {
  const { reportDto } = useReportsContext();
  const { reportId } = useReportId();
  const { t } = useTranslation();
  const { timezone } = useTimezone();
  const { layout } = reportDto || {};
  // The grid's own table instance drives the exported ROWS (sorted, un-paginated). Column
  // selection/order comes from the recoil state below — never from the table's stable reference,
  // which would freeze the export at the first render's columns.
  const { table } = useContext(TableOptionsContext);

  // Same source of truth the grid and the server-side CSV use: columnOrder holds the active columns
  // in the user's chosen order, and columnVisibility flags the ones switched off in the Manage
  // Columns drawer. Reading these (rather than the stable `table` ref) is what makes the export
  // react to every drawer change, so the CSV matches exactly what is on screen (RYDE-7777).
  const columnOrder = useRecoilValue(reportsTableColumnsOrderStateAF(reportId));
  const columnVisibility = useRecoilValue(
    reportsTableColumnsVisibilityAF(reportId),
  );
  const columnSorting = useRecoilValue(reportsTableColumnsSortingAF(reportId));
  // The grid filters its rows by this search box (report-grid feeds the filtered set into the
  // table), so the export must re-derive when it changes — otherwise the CSV keeps the pre-search
  // rows while the grid shows the narrowed set.
  const searchValue = useRecoilValue(rideFilterSearchAtom(reportId));

  const columns = useMemo(() => {
    return layout?.columns || [];
  }, [layout?.columns]);

  // Visible columns in on-screen order; internal helper columns (isHiddenFromView) are dropped.
  const exportColumns = useMemo(
    () => getReportExportColumns({ columns, columnOrder, columnVisibility }),
    [columns, columnOrder, columnVisibility],
  );

  const headers = useMemo((): LabelKeyObject[] => {
    return exportColumns.map(
      ({ uiTableColumn, columnTitleTranslationKey }) => ({
        key: uiTableColumn,
        label: columnTitleTranslationKey
          ? t(columnTitleTranslationKey)
          : t(`gridLabel.${uiTableColumn}`),
      }),
    );
  }, [exportColumns, t]);

  const tableDataRows = useMemo(() => {
    if (!table || !exportColumns.length) {
      return [];
    }

    // Pre-pagination rows = every filtered row in the grid's current sort order (not just the
    // current page). `columnSorting`, `searchValue` and `reportDto?.rows` are dependencies so a
    // re-sort, a search, or new report data re-derives the export from the (stable) table ref.
    return table.getPrePaginatedRowModel().rows.map(row => {
      const rowValues: Record<string, unknown> = {};
      exportColumns.forEach(reportGridColumn => {
        rowValues[reportGridColumn.uiTableColumn] = formatReportExportCellValue(
          {
            value: row.getValue(reportGridColumn.uiTableColumn),
            reportGridColumn,
            timezone,
            t,
          },
        );
      });
      return rowValues;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    columnSorting,
    exportColumns,
    reportDto?.rows,
    searchValue,
    t,
    table,
    timezone,
  ]);

  const footer = useMemo(() => {
    const footersRow: Record<string, unknown> = {};

    if (!reportDto || !exportColumns.length) {
      return footersRow;
    }

    exportColumns.forEach(reportGridColumn => {
      const { uiTableColumn, prefixTotalTranslationKey, prefixTotalDelimiter } =
        reportGridColumn;
      const columnFooterColumnTextTranslated = prefixTotalTranslationKey
        ? t(prefixTotalTranslationKey)
        : "";

      const footerValue = getReportFooterValue({
        rows: reportDto.rows,
        reportGridColumn,
        timezone,
        t,
        isExport: true,
      });

      footersRow[uiTableColumn] =
        `${columnFooterColumnTextTranslated} ` +
        `${prefixTotalDelimiter ? prefixTotalDelimiter : ""} ${footerValue}`;
    });

    return footersRow;
  }, [exportColumns, reportDto, t, timezone]);

  return { headers, footer, tableDataRows };
};
